import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "motion/react";
import {
  Activity,
  CalendarHeart,
  Coins,
  TrendingUp,
  Radar as RadarIcon,
  Bell,
  Sparkles,
  ArrowRight,
  Wand2,
  BookOpen,
  Lock,
  Plug,
  Timer,
  ThumbsUp,
} from "lucide-react";
import { useCompany, useCompanyId } from "@/lib/CompanyContext";
import { CalendarBoard } from "@/components/CalendarBoard";
import { formatCreditsShort } from "@/lib/format";
import { companiesApi } from "@/api/companies";
import { issuesApi } from "@/api/issues";
import { routinesApi, type Routine } from "@/api/routines";
import { escalaApi } from "@/api/escala";
import { authApi } from "@/lib/auth";
import { queryKeys } from "@/lib/queryKeys";
import { HeroScene } from "@/components/HeroScene";
import { Card, CardLabel } from "@/components/ui/Card";
import { StartHere } from "@/components/ui";
import { escalaPct } from "@/lib/escalaScore";

type TabKey = "hoje" | "acompanhamento";

// formatBRL local removido — agora usa formatCredits do lib/format.

function getGreeting(date = new Date()): string {
  const h = date.getHours();
  if (h < 12) return "Bom dia";
  if (h < 18) return "Boa tarde";
  return "Boa noite";
}

function formatLongDate(date = new Date()): string {
  return new Intl.DateTimeFormat("pt-BR", {
    weekday: "long",
    day: "numeric",
    month: "long",
  }).format(date);
}

/** Hero banner cinematográfico — constelação Atlas + greeting dinâmico + pulse live */
function HeroBanner({
  firstName,
  companyName,
  inProgress,
  inReview,
}: {
  firstName: string;
  companyName: string;
  inProgress: number;
  inReview: number;
}) {
  const greeting = getGreeting();
  const date = formatLongDate();

  return (
    <div className="wf-hero" style={{ minHeight: 240 }}>
      {/* Banner 2D — HeroScene controla próprio fundo + atmosfera.
          Sem overlay externo, senão escurece tudo. */}
      <HeroScene />

      <div className="wf-hero__content">
        <motion.h1
          className="wf-hero__greeting"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
        >
          {greeting}, {firstName}
        </motion.h1>
        <motion.p
          className="wf-hero__sub"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, delay: 0.08, ease: [0.16, 1, 0.3, 1] }}
        >
          {companyName} · {date}
        </motion.p>

        <motion.div
          className="wf-hero__pulse"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, delay: 0.18, ease: [0.16, 1, 0.3, 1] }}
        >
          <span className="wf-hero__pulse-dot" />
          <span>
            {inProgress > 0
              ? `${inProgress} ${inProgress === 1 ? "tarefa" : "tarefas"} rodando agora`
              : "nenhuma tarefa rodando agora"}
            {inReview > 0 && ` · ${inReview} ${inReview === 1 ? "pronta" : "prontas"} pra revisar`}
          </span>
        </motion.div>
      </div>
    </div>
  );
}

/** Mini radar ESCALA (6 eixos) — preview no card de Briefing strength */
function MiniRadar({ scores }: { scores: number[] }) {
  const cx = 40;
  const cy = 40;
  const r = 30;
  const angleStep = (Math.PI * 2) / scores.length;
  const points = scores
    .map((s, i) => {
      const angle = -Math.PI / 2 + i * angleStep;
      const dist = (s / 100) * r;
      return `${cx + dist * Math.cos(angle)},${cy + dist * Math.sin(angle)}`;
    })
    .join(" ");
  const ringPoints = (mult: number) =>
    Array.from({ length: scores.length }, (_, i) => {
      const angle = -Math.PI / 2 + i * angleStep;
      return `${cx + r * mult * Math.cos(angle)},${cy + r * mult * Math.sin(angle)}`;
    }).join(" ");

  return (
    <svg width={80} height={80}>
      <polygon points={ringPoints(1)} fill="none" stroke="var(--wf-ink-5)" strokeWidth={1} />
      <polygon points={ringPoints(0.66)} fill="none" stroke="var(--wf-ink-5)" strokeWidth={1} />
      <polygon points={ringPoints(0.33)} fill="none" stroke="var(--wf-ink-5)" strokeWidth={1} />
      <polygon points={points} fill="rgba(255,112,0,0.18)" stroke="var(--nd-orange)" strokeWidth={1.5} />
    </svg>
  );
}

/** Mapeia título da routine pra ícone + cor — espelha o RITUAL_LIBRARY de
 *  Automacoes. Paleta MIV: APENAS navy + orange. */
const RITUAL_VISUAL: Record<
  string,
  { icon: typeof BookOpen; accent: string }
> = {
  "Aprende com seus aprovados": { icon: BookOpen, accent: "#063e6e" }, // ND navy
  "Relatório semanal de campanhas": { icon: TrendingUp, accent: "#ff7000" },
  "Lembrete de revisão pendente": { icon: Bell, accent: "#ff7000" },
  "Relatório semanal de custos": { icon: Coins, accent: "#ff7000" },
};

function formatNextRun(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso).getTime();
  const diffMs = d - Date.now();
  if (diffMs < 0) return "agora";
  if (diffMs < 3600_000) return `em ${Math.round(diffMs / 60_000)}min`;
  if (diffMs < 86_400_000) return `em ${Math.round(diffMs / 3_600_000)}h`;
  return `em ${Math.round(diffMs / 86_400_000)}d`;
}

/** Card do radar ESCALA com dados reais — substitui o "Briefing strength" mock. */
function EscalaRadarCard({
  recs,
}: {
  recs:
    | {
        hasAssessment: boolean;
        scores?: { E: number; S: number; C: number; A: number; L: number; A2: number };
        recommendations?: Array<{ letter: string; score: number; letterName: string }>;
      }
    | undefined;
}) {
  if (!recs?.hasAssessment || !recs.scores) {
    return (
      <Card delay={0.2}>
        <CardLabel icon={<RadarIcon size={13} strokeWidth={1.75} color="var(--nd-orange)" />}>
          Radar ESCALA
        </CardLabel>
        <p style={{ fontSize: 13, color: "var(--wf-ink-2)", lineHeight: 1.5, marginBottom: 12 }}>
          Faça o diagnóstico ESCALA pra ver onde sua empresa está nas 6 dimensões e
          atacar o gap real primeiro.
        </p>
        <Link
          to="/escala"
          className="wf-btn wf-btn--sm"
          style={{ textDecoration: "none" }}
        >
          Fazer diagnóstico <ArrowRight size={11} strokeWidth={2} />
        </Link>
      </Card>
    );
  }

  const scores = recs.scores;
  // Exibição 0–90 (mesmo mapa do dashboard ESCALA): gabaritar = 90%, sempre
  // sobra 10% pra evoluir. Sem isso o painel mostrava 0–100 (inconsistente).
  const orderedScores = [scores.E, scores.S, scores.C, scores.A, scores.L, scores.A2].map(escalaPct);
  const avg = Math.round(orderedScores.reduce((a, b) => a + b, 0) / 6);
  const weakest = recs.recommendations?.[0] ?? null;

  return (
    <Card delay={0.2}>
      <CardLabel icon={<RadarIcon size={13} strokeWidth={1.75} color="var(--nd-orange)" />}>
        Radar ESCALA
      </CardLabel>
      <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
        <MiniRadar scores={orderedScores} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="wf-display" style={{ fontSize: 22, fontWeight: 700, lineHeight: 1.1 }}>
            {avg}%
          </div>
          <div style={{ fontSize: 11, color: "var(--wf-ink-3)", marginBottom: 8 }}>
            maturidade média
            {weakest && (
              <>
                {" · maior gap em "}
                <strong style={{ color: "var(--wf-ink)" }}>{weakest.letterName}</strong>
              </>
            )}
          </div>
          <Link
            to="/escala"
            style={{
              fontSize: 12,
              color: "var(--nd-orange-pun)",
              fontWeight: 600,
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              textDecoration: "none",
            }}
          >
            ver radar <ArrowRight size={11} strokeWidth={2} />
          </Link>
        </div>
      </div>
    </Card>
  );
}

type DashboardKpis = NonNullable<
  ReturnType<typeof useQuery<Awaited<ReturnType<typeof companiesApi.dashboard>>>>["data"]
>["kpis"];

function ApprovalRateCard({ kpis }: { kpis: DashboardKpis | undefined }) {
  const rate = kpis?.approvalRatePercent;
  const sample = kpis?.approvalSampleSize ?? 0;
  return (
    <Card delay={0.3}>
      <CardLabel icon={<ThumbsUp size={13} strokeWidth={1.75} color="var(--nd-orange)" />}>
        Taxa de aprovação
      </CardLabel>
      <div className="wf-display" style={{ fontSize: 36, lineHeight: 1, marginBottom: 6, fontWeight: 700 }}>
        {rate === null || rate === undefined ? "—" : `${rate}%`}
      </div>
      <div style={{ fontSize: 12, color: "var(--wf-ink-3)" }}>
        {sample === 0
          ? "ainda sem entregas pra medir"
          : `${sample} entrega${sample === 1 ? "" : "s"} nos últimos 30 dias`}
      </div>
    </Card>
  );
}

function DeliveryTimeCard({ kpis }: { kpis: DashboardKpis | undefined }) {
  const ms = kpis?.avgDeliveryMs;
  const sample = kpis?.deliverySampleSize ?? 0;
  const display = ms === null || ms === undefined
    ? "—"
    : ms < 60_000
      ? `${Math.round(ms / 1000)}s`
      : ms < 3_600_000
        ? `${Math.round(ms / 60_000)}min`
        : `${(ms / 3_600_000).toFixed(1)}h`;
  return (
    <Card delay={0.35}>
      <CardLabel icon={<Timer size={13} strokeWidth={1.75} color="var(--nd-orange)" />}>
        Tempo médio de entrega
      </CardLabel>
      <div className="wf-display" style={{ fontSize: 32, lineHeight: 1, marginBottom: 6, fontWeight: 700 }}>
        {display}
      </div>
      <div style={{ fontSize: 12, color: "var(--wf-ink-3)" }}>
        {sample === 0
          ? "ainda sem entregas finalizadas"
          : `média das últimas ${sample}`}
      </div>
    </Card>
  );
}

function IntegrationsCard({ kpis }: { kpis: DashboardKpis | undefined }) {
  const count = kpis?.connectedIntegrations ?? 0;
  return (
    <Card delay={0.4}>
      <CardLabel icon={<Plug size={13} strokeWidth={1.75} color="var(--nd-orange)" />}>
        Integrações conectadas
      </CardLabel>
      <div className="wf-display" style={{ fontSize: 36, lineHeight: 1, marginBottom: 6, fontWeight: 700 }}>
        {count}
      </div>
      <div style={{ fontSize: 12, color: "var(--wf-ink-3)", marginBottom: 12 }}>
        {count === 0
          ? "comece conectando Meta Ads ou Google Ads"
          : count === 1
            ? "1 ferramenta plugada"
            : `${count} ferramentas plugadas`}
      </div>
      <Link
        to="/integracoes"
        style={{
          fontSize: 12,
          color: "var(--nd-orange-pun)",
          fontWeight: 600,
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          textDecoration: "none",
        }}
      >
        gerenciar <ArrowRight size={11} strokeWidth={2} />
      </Link>
    </Card>
  );
}

/** Card de overview das automações ativas no Painel */
function RituaisAtivosCard({ routines }: { routines: Routine[] }) {
  const active = routines.filter((r) => r.status === "active");
  const next = active
    .map((r) => ({ routine: r, next: r.triggers[0]?.nextRunAt ?? null }))
    .filter((x) => x.next)
    .sort((a, b) => (a.next! < b.next! ? -1 : 1))[0];

  return (
    <Card delay={0.25}>
      <CardLabel icon={<Wand2 size={13} strokeWidth={1.75} color="var(--nd-orange)" />}>
        Rituais ativos
      </CardLabel>

      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: 8,
          marginBottom: 10,
        }}
      >
        <div
          className="wf-display"
          style={{ fontSize: 36, lineHeight: 1, fontWeight: 700 }}
        >
          {active.length}
        </div>
        <div style={{ fontSize: 12, color: "var(--wf-ink-3)" }}>
          {active.length === 1 ? "ritual rodando sozinho" : "rituais rodando sozinhos"}
        </div>
      </div>

      {/* Lista compacta dos ativos */}
      {active.length > 0 ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 6,
            marginBottom: 12,
          }}
        >
          {active.slice(0, 3).map((r) => {
            const visual = RITUAL_VISUAL[r.title] ?? { icon: Wand2, accent: "#ff7000" };
            const Icon = visual.icon;
            return (
              <div
                key={r.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  fontSize: 12,
                  color: "var(--wf-ink-2)",
                }}
              >
                <div
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: 6,
                    background: `${visual.accent}18`,
                    color: visual.accent,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Icon size={11} strokeWidth={1.75} />
                </div>
                <span
                  style={{
                    flex: 1,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    color: "var(--wf-ink)",
                    fontWeight: 500,
                  }}
                  title={r.title}
                >
                  {r.title}
                </span>
                {r.protected && (
                  <Lock size={10} strokeWidth={2} color="var(--wf-ink-3)" />
                )}
              </div>
            );
          })}
          {active.length > 3 && (
            <span style={{ fontSize: 11, color: "var(--wf-ink-3)" }}>
              + {active.length - 3} outros
            </span>
          )}
        </div>
      ) : (
        <p
          style={{
            fontSize: 12,
            color: "var(--wf-ink-3)",
            marginBottom: 12,
            lineHeight: 1.5,
          }}
        >
          nenhum ativo ainda. ative templates pra ter coisas rodando enquanto você descansa
        </p>
      )}

      {/* Próximo run + CTA */}
      <div
        style={{
          marginTop: "auto",
          paddingTop: 10,
          borderTop: "1px dashed var(--wf-ink-5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
        }}
      >
        {next ? (
          <span style={{ fontSize: 11, color: "var(--wf-ink-3)" }}>
            próximo {formatNextRun(next.next)}
          </span>
        ) : (
          <span />
        )}
        <Link
          to="/automacoes"
          style={{
            fontSize: 12,
            color: "var(--nd-orange-pun)",
            fontWeight: 600,
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            textDecoration: "none",
          }}
        >
          ver todos <ArrowRight size={11} strokeWidth={2} />
        </Link>
      </div>
    </Card>
  );
}

export default function Painel() {
  const { company, isLoading: companyLoading } = useCompany();
  const companyId = useCompanyId();
  const [tab, setTab] = useState<TabKey>("hoje");

  const session = useQuery({
    queryKey: ["auth", "session"],
    queryFn: () => authApi.getSession(),
  });

  const dashboard = useQuery({
    queryKey: companyId
      ? queryKeys.companies.dashboard(companyId)
      : ["companies", "none", "dashboard"],
    queryFn: () => companiesApi.dashboard(companyId!),
    enabled: !!companyId,
    // KPI "tarefas rodando" precisa atualizar enquanto o user fica no painel
    refetchInterval: 4000,
  });

  const inProgressIssues = useQuery({
    queryKey: companyId
      ? queryKeys.companies.issues(companyId, { status: "in_progress" })
      : ["companies", "none", "issues", { status: "in_progress" }],
    queryFn: () => issuesApi.list(companyId!, { status: "in_progress", limit: 5 }),
    enabled: !!companyId,
    refetchInterval: 4000,
  });

  const routinesList = useQuery({
    queryKey: ["companies", companyId, "routines"],
    queryFn: () => routinesApi.list(companyId!),
    enabled: !!companyId,
  });

  const escalaRecs = useQuery({
    queryKey: ["companies", companyId, "escala", "recommendations"],
    queryFn: () => escalaApi.recommendations(companyId!),
    enabled: !!companyId,
    staleTime: 60_000,
  });

  const firstName = useMemo(
    () => session.data?.user.name?.split(" ")[0] ?? "por aí",
    [session.data?.user.name],
  );

  if (companyLoading) {
    return (
      <div className="wf" style={{ padding: 32 }}>
        <span className="wf-hand-note">carregando empresa…</span>
      </div>
    );
  }

  if (!company) {
    return (
      <div style={{ padding: 32 }}>
        <Card>
          <CardLabel icon={<Sparkles size={13} color="var(--nd-orange)" />}>
            Preparando workspace
          </CardLabel>
          <p style={{ fontSize: 13, color: "var(--wf-ink-2)" }}>
            Estamos preparando sua empresa. Se a tela não atualizar em alguns segundos, recarregue.
          </p>
        </Card>
      </div>
    );
  }

  const monthBudget = dashboard.data?.costs.monthBudgetCents ?? 0;
  const monthSpend = dashboard.data?.costs.monthSpendCents ?? 0;
  const utilization = dashboard.data?.costs.monthUtilizationPercent ?? 0;
  const tasks = dashboard.data?.tasks ?? {};
  const inProgressCount = (tasks.in_progress as number) ?? 0;
  const inReviewCount = (tasks.in_review as number) ?? 0;

  return (
    <div>
      {/* Hero banner */}
      <HeroBanner
        firstName={firstName}
        companyName={company.name}
        inProgress={inProgressCount}
        inReview={inReviewCount}
      />

      {/* Tabs */}
      <div className="wf-tabs">
        <button
          type="button"
          className={`wf-tab ${tab === "hoje" ? "wf-tab--active" : ""}`}
          onClick={() => setTab("hoje")}
        >
          Hoje
        </button>
        <button
          type="button"
          className={`wf-tab ${tab === "acompanhamento" ? "wf-tab--active" : ""}`}
          onClick={() => setTab("acompanhamento")}
        >
          Acompanhamento
        </button>
      </div>

      {/* Content */}
      <div style={{ padding: "24px 32px 48px", maxWidth: 1280, margin: "0 auto" }}>
        {tab === "hoje" ? (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
              gap: 14,
            }}
          >
            {/* Próxima mentoria (Daniel Silva) — primeiro card por destaque */}
            <Card delay={0} style={{ background: "var(--nd-blue-deep)", color: "#fff", borderColor: "var(--nd-blue-deep)" }}>
              <CardLabel icon={<CalendarHeart size={13} strokeWidth={1.75} color="var(--nd-orange)" />}>
                <span style={{ color: "rgba(255,255,255,0.7)" }}>Próxima mentoria</span>
              </CardLabel>
              <div className="wf-display" style={{ fontSize: 22, lineHeight: 1.1, marginBottom: 4, fontWeight: 700 }}>
                Daniel Silva
              </div>
              <div style={{ fontSize: 12, color: "rgba(255,255,255,0.65)", marginBottom: 16 }}>
                quinta-feira · 15h00 · 1h
              </div>
              <a
                href="https://app.themembers.com.br"
                target="_blank"
                rel="noreferrer"
                className="wf-btn wf-btn--sm"
                style={{
                  background: "var(--nd-orange)",
                  color: "#fff",
                  borderColor: "#1a1a1a",
                  textDecoration: "none",
                }}
              >
                Adicionar ao calendário
              </a>
            </Card>

            {/* Em execução agora */}
            <Card delay={0.05}>
              <CardLabel icon={<Activity size={13} strokeWidth={1.75} color="var(--nd-orange)" />}>
                Em execução agora
              </CardLabel>
              <div
                className="wf-display"
                style={{ fontSize: 36, lineHeight: 1, marginBottom: 6, fontWeight: 700 }}
              >
                {inProgressCount}
              </div>
              <div style={{ fontSize: 12, color: "var(--wf-ink-3)", marginBottom: 14 }}>
                {inProgressCount === 1 ? "tarefa rodando" : "tarefas rodando"}
              </div>
              {(inProgressIssues.data ?? []).slice(0, 3).map((issue) => (
                <Link
                  key={issue.id}
                  to={
                    issue.status === "done" || issue.status === "in_review"
                      ? `/resultado/${issue.id}`
                      : `/execucao/${issue.id}`
                  }
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "8px 0",
                    borderTop: "1px dashed var(--wf-ink-5)",
                    fontSize: 12,
                    color: "var(--wf-ink)",
                    textDecoration: "none",
                  }}
                >
                  <span
                    className="wf-hero__pulse-dot"
                    style={{ width: 6, height: 6, background: "var(--nd-orange)" }}
                  />
                  <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {issue.title}
                  </span>
                </Link>
              ))}
              {inProgressCount === 0 && (
                <div style={{ fontSize: 12, color: "var(--wf-ink-3)" }}>
                  ninguém rodando. que tal{" "}
                  <Link to="/nova-tarefa" style={{ color: "var(--nd-orange-pun)" }}>
                    criar uma tarefa
                  </Link>
                  ?
                </div>
              )}
            </Card>

            {/* Créditos do mês */}
            <Link
              to="/consumo"
              style={{ textDecoration: "none", color: "inherit", display: "block" }}
              title="Ver detalhamento do consumo"
            >
              <Card
                delay={0.1}
                style={{
                  cursor: "pointer",
                  transition: "border-color 120ms, transform 120ms",
                }}
                className="wf-card-hover"
              >
                <CardLabel icon={<Coins size={13} strokeWidth={1.75} color="var(--nd-orange)" />}>
                  ND Credits usados
                </CardLabel>
                <div
                  className="wf-display"
                  style={{ fontSize: 32, lineHeight: 1, marginBottom: 4, fontWeight: 700 }}
                >
                  {formatCreditsShort(monthSpend)}
                </div>
                <div style={{ fontSize: 12, color: "var(--wf-ink-3)", marginBottom: 12 }}>
                  de {formatCreditsShort(monthBudget)} créditos ({utilization.toFixed(0)}% usados)
                </div>
                <div className="wf-progress">
                  <span style={{ width: `${Math.min(100, utilization)}%` }} />
                </div>
                <div
                  style={{
                    fontSize: 11,
                    color: "var(--nd-orange)",
                    marginTop: 10,
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                  }}
                >
                  Ver detalhamento →
                </div>
              </Card>
            </Link>

            {/* ESCALA radar — substitui Briefing strength com dados reais */}
            <EscalaRadarCard recs={escalaRecs.data} />

            {/* Rituais ativos — overview das automações */}
            <RituaisAtivosCard routines={routinesList.data ?? []} />

            {/* Taxa de aprovação — quantos cards o user aprova de primeira */}
            <ApprovalRateCard kpis={dashboard.data?.kpis} />

            {/* Tempo médio de entrega */}
            <DeliveryTimeCard kpis={dashboard.data?.kpis} />

            {/* Integrações conectadas */}
            <IntegrationsCard kpis={dashboard.data?.kpis} />
          </div>
        ) : (
          <AcompanhamentoTab companyId={companyId} />
        )}

        {tab === "hoje" && (
          <div style={{ marginTop: 28 }}>
            <StartHere
              hasAssessment={escalaRecs.data?.hasAssessment ?? false}
              recommendations={
                escalaRecs.data?.hasAssessment ? escalaRecs.data.recommendations : []
              }
            />
          </div>
        )}
      </div>
    </div>
  );
}

/** Tab "Acompanhamento" — termômetros de aderência (post calendar, campanhas, etc).
 *  Hoje é empty state com explicação. Conforme integrações forem ligadas,
 *  cada uma vira um termômetro vivo aqui. */
function AcompanhamentoTab({ companyId }: { companyId: string | null }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
    >
      {companyId ? (
        <CalendarBoard companyId={companyId} />
      ) : (
        <div className="wf-hand-note" style={{ padding: 24 }}>carregando…</div>
      )}
    </motion.div>
  );
}
